import { toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { AnalyticsProvider, Main, TBgColor, ToastProvider } from 'telia-front-react';
import CookieConsentStore from '../stores/CookieConsentStore';
import { TranslationsContext } from '@teliaee/telia-translate';
import ContentStore from '../stores/ContentStore';
import GaDataStore from '../stores/GaDataStore';

interface MainProps {
  children?: React.ReactChild | React.ReactChild[];
  cookieConsentStore?: CookieConsentStore;
  contentStore?: ContentStore;
  gaDataStore?: GaDataStore;
}

function MainWrapper(props: MainProps): JSX.Element {
  const translateGlobal = React.useContext(TranslationsContext).translateGlobal;
  const { cookieConsentStore } = props;

  const [rendered, setRendered] = useState<boolean>(false);

  const consentContent = {
    title: translateGlobal('cookieagreement.notice.title'),
    description: translateGlobal('cookieagreement.notice.description'),
    button: {
      text: translateGlobal('cookieagreement.notice.button'),
      onClick: () => window.Cookiebot.show(),
    },
    categories: {
      statistics: translateGlobal('cookieagreement.statistics'),
      necessary: translateGlobal('cookieagreement.necessary'),
      marketing: translateGlobal('cookieagreement.marketing'),
      preferences: translateGlobal('cookieagreement.preferences'),
    },
  };

  const getBackgroundColor = (): TBgColor => {
    return (props.contentStore.currentPageContent?.backgroundColor as TBgColor) || undefined;
  };

  const renderWithAnalyticsProvider = (children: React.ReactNode) => {
    return (
      <AnalyticsProvider
        event="AutoEvent"
        page={props.gaDataStore.getPageTitle()}
        onPush={async (data) =>
          window.dataLayer?.push({
            event: data.event,
            eventCategory: data.page,
            eventLabel: data.id,
            eventAction: data.group,
          })
        }
      >
        <ToastProvider>{children}</ToastProvider>
      </AnalyticsProvider>
    );
  };

  useEffect(() => {
    setRendered(true);
  }, []);

  if (!rendered) {
    return (
      <Main background={getBackgroundColor()} consentContent={consentContent} consentEnabled={true} consent={toJS(cookieConsentStore?.cookieConsents)}>
        {renderWithAnalyticsProvider(props.children)}
      </Main>
    );
  }

  return (
    <Main
      background={getBackgroundColor()}
      consentContent={consentContent}
      consentEnabled={toJS(cookieConsentStore?.isCookiebotEnabled)}
      consent={toJS(cookieConsentStore?.cookieConsents)}
    >
      {renderWithAnalyticsProvider(props.children)}
    </Main>
  );
}

export default inject('cookieConsentStore', 'contentStore', 'gaDataStore')(observer(MainWrapper));
